import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import Layout from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

function CrudSection({ title, queryKey, endpoint, fields, columns, testidPrefix, renderExtra }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { data: rows = [] } = useQuery({
    queryKey: [queryKey],
    queryFn: async () => (await api.get(endpoint)).data,
  });

  const openCreate = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, form);
        toast.success(`${title} updated`);
      } else {
        await api.post(endpoint, form);
        toast.success(`${title} created`);
      }
      setOpen(false);
      setForm({});
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success(`${title} deleted`);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid={`${testidPrefix}-section`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">{title}s</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid={`add-${testidPrefix}-btn`} onClick={openCreate}
              className="bg-slate-950 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4" /> Add {title}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-md">
            <DialogHeader><DialogTitle>{editing ? `Edit ${title}` : `Add ${title}`}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              {fields.map((f) => (
                <div key={f.name} className="space-y-1.5">
                  <Label>{f.label}</Label>
                  {f.type === "multiselect" ? (
                    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-${f.name}-multiselect`}>
                      {(f.options || []).map((o) => {
                        const cur = form[f.name] || [];
                        const on = cur.includes(o.value);
                        return (
                          <button type="button" key={o.value} data-testid={`${testidPrefix}-${f.name}-opt-${o.value}`}
                            onClick={() => setForm({ ...form, [f.name]: on ? cur.filter((x) => x !== o.value) : [...cur, o.value] })}
                            className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${on
                              ? "bg-slate-950 text-white border-slate-950"
                              : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : f.type === "select" ? (
                    <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                      <SelectTrigger data-testid={`${testidPrefix}-${f.name}-select`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => (
                          <SelectItem key={o.value} value={o.value} data-testid={`${testidPrefix}-${f.name}-opt-${o.value}`}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input data-testid={`${testidPrefix}-${f.name}-input`} type={f.type || "text"} required={f.required !== false}
                      value={form[f.name] || ""}
                      onChange={(e) => setForm({ ...form, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })} />
                  )}
                </div>
              ))}
              <Button type="submit" data-testid={`${testidPrefix}-submit-btn`} disabled={loading}
                className="w-full bg-slate-950 hover:bg-slate-800 text-white">
                {loading ? "Saving…" : editing ? `Save ${title}` : `Create ${title}`}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {renderExtra}
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
            <TableHead className="w-24"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-slate-400 py-8" data-testid={`${testidPrefix}-empty`}>
              No {title.toLowerCase()}s yet.
            </TableCell></TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id} data-testid={`${testidPrefix}-row-${row.id}`}>
              {columns.map((c) => <TableCell key={c.key}>{c.render ? c.render(row) : row[c.key]}</TableCell>)}
              <TableCell>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" data-testid={`edit-${testidPrefix}-${row.id}`}
                    onClick={() => openEdit(row)} className="text-slate-400 hover:text-slate-950 hover:bg-slate-100">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`delete-${testidPrefix}-${row.id}`}
                    onClick={() => remove(row.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Structure() {
  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => (await api.get("/admin/departments")).data,
  });
  const { data: semesters = [] } = useQuery({
    queryKey: ["semesters"],
    queryFn: async () => (await api.get("/admin/semesters")).data,
  });
  const { data: teachers = [] } = useQuery({
    queryKey: ["teachers"],
    queryFn: async () => (await api.get("/admin/teachers")).data,
  });

  const [subjectDept, setSubjectDept] = useState("");

  const deptOptions = departments.map((d) => ({ value: d.id, label: `${d.name} (${d.code})` }));
  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";
  const semName = (id) => semesters.find((s) => s.id === id)?.name || "—";

  return (
    <Layout title="Academic Structure">
      <Tabs defaultValue="departments">
        <TabsList className="bg-white border border-slate-200" data-testid="structure-tabs">
          <TabsTrigger value="departments" data-testid="tab-departments">Departments</TabsTrigger>
          <TabsTrigger value="semesters" data-testid="tab-semesters">Semesters</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="tab-subjects">Subjects</TabsTrigger>
          <TabsTrigger value="periods" data-testid="tab-periods">Periods</TabsTrigger>
        </TabsList>

        <TabsContent value="departments" className="mt-6">
          <CrudSection title="Department" queryKey="departments" endpoint="/admin/departments" testidPrefix="department"
            fields={[
              { name: "name", label: "Department name" },
              { name: "code", label: "Code (e.g. CSE)" },
            ]}
            columns={[{ key: "name", label: "Name" }, { key: "code", label: "Code" }]} />
        </TabsContent>

        <TabsContent value="semesters" className="mt-6">
          <CrudSection title="Semester" queryKey="semesters" endpoint="/admin/semesters" testidPrefix="semester"
            fields={[
              { name: "name", label: "Semester name (e.g. Semester 3)" },
              { name: "number", label: "Semester number", type: "number" },
              { name: "department_id", label: "Department", type: "select", options: deptOptions },
            ]}
            columns={[
              { key: "name", label: "Name" },
              { key: "number", label: "No." },
              { key: "department_id", label: "Department", render: (r) => deptName(r.department_id) },
            ]} />
        </TabsContent>

        <TabsContent value="subjects" className="mt-6">
          <CrudSection title="Subject" queryKey="subjects" endpoint="/admin/subjects" testidPrefix="subject"
            fields={[
              { name: "name", label: "Subject name" },
              { name: "code", label: "Subject code" },
              { name: "department_id", label: "Department", type: "select", options: deptOptions },
              { name: "semester_id", label: "Semester", type: "select",
                options: semesters.filter((s) => !subjectDept || s.department_id === subjectDept).map((s) => ({ value: s.id, label: `${s.name} — ${deptName(s.department_id)}` })) },
              { name: "teacher_ids", label: "Teachers (optional — multiple can be assigned; each marks their own classes)", type: "multiselect", required: false,
                options: teachers.map((t) => ({ value: t.id, label: `${t.name} — ${deptName(t.department_id)}` })) },
              { name: "paper_type", label: "Paper type", type: "select",
                options: [{ value: "core", label: "Core paper (all students mapped automatically)" }, { value: "optional", label: "Optional paper (elective)" }] },
              { name: "elective_group", label: "Elective group name (only for optional papers, e.g. Elective I)", required: false },
            ]}
            columns={[
              { key: "name", label: "Name" },
              { key: "code", label: "Code" },
              { key: "paper_type", label: "Type", render: (r) => r.paper_type === "optional" ? `Optional${r.elective_group ? ` · ${r.elective_group}` : ""}` : "Core" },
              { key: "department_id", label: "Department", render: (r) => deptName(r.department_id) },
              { key: "semester_id", label: "Semester", render: (r) => semName(r.semester_id) },
              { key: "teacher_name", label: "Teacher" },
            ]} />
        </TabsContent>

        <TabsContent value="periods" className="mt-6">
          <CrudSection title="Period" queryKey="periods" endpoint="/admin/periods" testidPrefix="period"
            fields={[
              { name: "label", label: "Label (e.g. Period 1)" },
              { name: "start_time", label: "Start time (e.g. 09:00)" },
              { name: "end_time", label: "End time (e.g. 09:50)" },
              { name: "order", label: "Order in day", type: "number" },
              { name: "department_id", label: "Department", type: "select", options: deptOptions },
            ]}
            columns={[
              { key: "order", label: "#" },
              { key: "label", label: "Label" },
              { key: "start_time", label: "Start" },
              { key: "end_time", label: "End" },
              { key: "department_id", label: "Department", render: (r) => deptName(r.department_id) },
            ]} />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
